/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { App, Drawer, Empty, Input, Menu } from 'antd';
import type { InputRef, MenuProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from './stores/common';
import { clearUndoHistory } from './undo/setWithUndo';
import * as Selector from './stores/selector';
import { usePrimitiveStore } from './stores/commonPrimitive';
import { useDataStore } from './stores/commonData';
import { ProjectState } from './types';
import { fetchProject, postFetch } from './cloudProjectUtil';
import { HOME_URL } from './constants';
import { useIsMobile } from './hooks';
import { askToSaveThen } from './saveReminder.tsx';
import {
  EXAMPLE_GROUPS,
  isExampleGroup as isGroup,
  type ExampleLeaf as Leaf,
  type ExampleGroup as Group,
} from './exampleCatalog';

// Left-edge drawer that mirrors the Examples submenu that used to live in
// the main menu. Opened from the toolbar button next to the Commons
// shortcut or from the main menu's Help -> Examples item; closed by any item
// click, clicking outside, or the close icon.
// The button source of truth is showExamplesDrawer on the primitive store.

interface Props {
  viewOnly: boolean;
}

// Canonical list of built-in examples lives in `exampleCatalog.ts` (shared
// with the agent's `listExampleProjects` tool). Aliased here as GROUPS to
// keep the rendering code below unchanged.
const GROUPS = EXAMPLE_GROUPS;

const TOP_LEVEL_KEYS = GROUPS.map((g) => g.key);

const RECENT_GROUP_KEY = 'recent';
const RECENT_KEY_PREFIX = 'recent/';
const MAX_RECENT = 5;

const DEFAULT_OPEN_KEYS = [RECENT_GROUP_KEY, ...TOP_LEVEL_KEYS];

// Persist which sections the user has expanded across drawer opens / page
// reloads, so a previously-opened subsection stays open instead of collapsing
// back to the default every time. Browser-local UI preference (per the same
// rationale as gallerySketchMode); kept out of the project state.
const OPEN_KEYS_STORAGE_KEY = 'aims-examples-drawer-open-keys';
const loadOpenKeys = (): string[] => {
  try {
    const raw = localStorage.getItem(OPEN_KEYS_STORAGE_KEY);
    if (!raw) return DEFAULT_OPEN_KEYS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((k) => typeof k === 'string') ? parsed : DEFAULT_OPEN_KEYS;
  } catch {
    return DEFAULT_OPEN_KEYS;
  }
};
const saveOpenKeys = (keys: string[]): void => {
  try {
    localStorage.setItem(OPEN_KEYS_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // localStorage unavailable / quota — non-fatal, just don't persist.
  }
};

// Persist the list's scroll position the same way, so a reload reopens the
// drawer where the user left it rather than at the top.
const SCROLL_TOP_STORAGE_KEY = 'aims-examples-drawer-scroll-top';
const loadScrollTop = (): number => {
  try {
    const v = Number(localStorage.getItem(SCROLL_TOP_STORAGE_KEY));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
};
const saveScrollTop = (y: number): void => {
  try {
    localStorage.setItem(SCROLL_TOP_STORAGE_KEY, String(Math.round(y)));
  } catch {
    // non-fatal
  }
};

const ExamplesDrawer = React.memo(({ viewOnly }: Props) => {
  const language = useStore(Selector.language);
  const recentlyOpened = useStore(Selector.recentlyOpenedExamples);
  const open = usePrimitiveStore(Selector.showExamplesDrawer);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const lang = useMemo(() => ({ lng: language }), [language]);

  // Search query — local state, cleared when the drawer closes so the user
  // re-enters at a clean slate. `userOpenKeys` tracks which sections the
  // user has chosen to expand/collapse; it's only honored when the query is
  // empty (during a search, we force-open every surviving group).
  const [query, setQuery] = useState('');
  // Seed from localStorage so a previously-expanded subsection is still open.
  const [userOpenKeys, setUserOpenKeys] = useState<string[]>(loadOpenKeys);
  const inputRef = useRef<InputRef>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollSaveTimerRef = useRef<number | undefined>(undefined);

  // Re-apply the saved scroll position. Called when the drawer opens and again
  // once its open animation settles: on the first open after a reload the inline
  // submenus may still be expanding, so the first attempt can be clamped short.
  const restoreScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const y = loadScrollTop();
    if (Math.abs(el.scrollTop - y) > 1) el.scrollTop = y;
  };

  useLayoutEffect(() => {
    if (open) restoreScroll();
  }, [open]);

  useEffect(() => () => window.clearTimeout(scrollSaveTimerRef.current), []);

  const setOpen = (v: boolean) =>
    usePrimitiveStore.getState().set((state) => {
      state.showExamplesDrawer = v;
    });

  const setProjectState = (projectState: ProjectState) => {
    useStore.getState().set((state) => {
      state.projectState = { ...projectState };
    });
    // Another project's undo entries must not act on the example.
    clearUndoHistory();
    usePrimitiveStore.getState().set((state) => {
      state.updateProjectsFlag = true;
      state.changed = false;
      state.resetSimulation = true;
      // Loading an example is a project open — preserve its saved piston plate
      // position instead of snapping the plate to the box top.
      state.resetKeepPiston = true;
      state.startSimulation = false;
    });
    useDataStore.getState().energyTimeSeries.clear();
    useDataStore.getState().speedArrayMap.clear();
    useDataStore.getState().positionTimeSeriesMap.clear();
  };

  const loadProject = (title: string) => {
    const owner = import.meta.env.VITE_EXAMPLE_PROJECT_OWNER;
    if (!title || !owner) return;
    // Loading an example replaces the whole projectState (see setProjectState,
    // which also clears `changed`), so warn before discarding unsaved edits —
    // the same gate the Open/Recent project menu items use.
    askToSaveThen(modal, () => {
      usePrimitiveStore.getState().setWaiting(true);
      fetchProject(owner, title, setProjectState).finally(() => {
        usePrimitiveStore.getState().setWaiting(false);
        postFetch();
      });
      if (useStore.getState().loggable) useStore.getState().logAction('Open Example: ' + title);
      useStore.getState().set((state) => {
        const next = [title, ...state.recentlyOpenedExamples.filter((t) => t !== title)];
        state.recentlyOpenedExamples = next.slice(0, MAX_RECENT);
      });
      if (!viewOnly) {
        window.history.pushState({}, document.title, HOME_URL);
      }
      setOpen(false);
    });
  };

  // Reset the search when the drawer is closed so the next open starts from a
  // clean search slate. The section expansion is deliberately NOT reset — it's
  // persisted (see below) so a previously-opened subsection stays open.
  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  // Persist the user's chosen expansion to localStorage whenever it changes.
  // `userOpenKeys` only tracks deliberate expand/collapse (the search force-open
  // path writes through `effectiveOpenKeys`, not here), so this stores intent.
  useEffect(() => {
    saveOpenKeys(userOpenKeys);
  }, [userOpenKeys]);

  // Filter the tree against the (case-insensitive) query, matching against
  // the localized label. A group passes if its own label matches (in which
  // case all descendants survive) or any descendant passes. Empty query →
  // full tree, no filtering. Returns the filtered tree plus the keys of all
  // surviving groups so the Menu can force-open them.
  const { filteredItems, surviveGroupKeys, hasMatches } = useMemo(() => {
    const q = query.trim().toLowerCase();

    const survivingKeys: string[] = [];

    const matchLabel = (labelKey: string) => t(labelKey, lang).toLowerCase().includes(q);

    const buildItem = (
      node: Leaf | Group,
      parentKey: string,
      forceInclude: boolean,
    ): NonNullable<MenuProps['items']>[number] | null => {
      if (isGroup(node)) {
        const k = node.key || `${parentKey}/${node.labelKey}`;
        const groupMatch = forceInclude || q === '' || matchLabel(node.labelKey);
        const childItems: NonNullable<MenuProps['items']> = [];
        for (const c of node.children) {
          const built = buildItem(c, k, groupMatch);
          if (built) childItems.push(built);
        }
        if (childItems.length === 0) return null;
        survivingKeys.push(k);
        return {
          key: k,
          label: t(node.labelKey, lang),
          children: childItems,
        };
      }
      const leafMatch = forceInclude || q === '' || matchLabel(node.labelKey);
      if (!leafMatch) return null;
      return {
        key: `${parentKey}/${node.title}`,
        label: t(node.labelKey, lang),
      };
    };

    const items: MenuProps['items'] = [];
    for (const g of GROUPS) {
      const built = buildItem(g, '', false);
      if (built) items.push(built);
    }
    return { filteredItems: items, surviveGroupKeys: survivingKeys, hasMatches: items.length > 0 };
  }, [query, t, lang]);

  // Flat key → project-title lookup so onClick can dispatch without walking
  // the tree on every click. Built once per language, independent of the
  // query (filtering removes entries from the visible menu but the lookup
  // still needs them when the filter is empty).
  const titleByKey = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: Array<Leaf | Group>, parentKey: string) => {
      nodes.forEach((node, i) => {
        if (isGroup(node)) {
          const k = node.key || `${parentKey}/g${i}`;
          walk(node.children, k);
        } else {
          map.set(`${parentKey}/${node.title}`, node.title);
        }
      });
    };
    GROUPS.forEach((g) => walk(g.children, g.key));
    return map;
  }, []);

  // title → labelKey lookup used to render the Recently Opened group, which
  // stores titles only (the canonical leaf identifier) but still needs the
  // localized label for display.
  const labelKeyByTitle = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: Array<Leaf | Group>) => {
      for (const node of nodes) {
        if (isGroup(node)) walk(node.children);
        else map.set(node.title, node.labelKey);
      }
    };
    GROUPS.forEach((g) => walk(g.children));
    return map;
  }, []);

  // Recently Opened group, shown only when the user isn't searching. Titles
  // that no longer exist in GROUPS (e.g. an example retired between sessions)
  // are silently skipped rather than rendered with a fallback label.
  const recentItems = useMemo(() => {
    if (query.trim() !== '') return [];
    return recentlyOpened
      .filter((title) => labelKeyByTitle.has(title))
      .map((title) => ({
        key: `${RECENT_KEY_PREFIX}${title}`,
        label: t(labelKeyByTitle.get(title) as string, lang),
      }));
  }, [query, recentlyOpened, labelKeyByTitle, t, lang]);

  const menuItems = useMemo(() => {
    if (recentItems.length === 0) return filteredItems;
    return [
      {
        key: RECENT_GROUP_KEY,
        label: t('examplesDrawer.RecentlyOpened', lang),
        children: recentItems,
      },
      ...(filteredItems ?? []),
    ];
  }, [filteredItems, recentItems, t, lang]);

  // During a search, force every surviving group open so matches are
  // immediately visible. With no query, defer to the user's saved expansion.
  const effectiveOpenKeys = query.trim() === '' ? userOpenKeys : surviveGroupKeys;

  const hasAnyMenuItems = recentItems.length > 0 || hasMatches;

  return (
    <Drawer
      title={t('menu.examplesSubMenu', lang)}
      placement="left"
      open={open}
      onClose={() => setOpen(false)}
      // On a phone, 92vw leaves ~8vw of dim background tappable so the user
      // can still close by tapping outside. 85vw was leaving the longest
      // 3-level labels (Materials Science → Ionic Crystals → …) wrapping
      // to two lines.
      width={isMobile ? '92vw' : 360}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { padding: '12px 16px' },
      }}
      destroyOnClose={false}
      // Bigger close icon on mobile — antd's default 16px hit area is
      // hard to land with a thumb.
      closeIcon={isMobile ? <span style={{ fontSize: 20, lineHeight: 1 }}>×</span> : undefined}
      // antd fires afterOpenChange once the open animation settles — that's
      // when input focus actually sticks (focusing during the transition is
      // racy because the drawer body isn't in the layout yet).
      afterOpenChange={(o) => {
        if (o) {
          restoreScroll();
          inputRef.current?.focus();
        }
      }}
    >
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Input
          ref={inputRef}
          allowClear
          placeholder={t('examplesDrawer.SearchPlaceholder', lang)}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Don't submit the menu on Enter — there's no obvious "first
          // match" concept here (the tree is grouped, not flat), so let the
          // user click the result they want.
        />
      </div>
      <div
        ref={scrollRef}
        style={{ flex: 1, overflowY: 'auto' }}
        // Only the unfiltered tree's position is worth remembering; a search
        // result list (or the clamp when the query resets on close) is not.
        onScroll={(e) => {
          if (!open || query.trim() !== '') return;
          const y = e.currentTarget.scrollTop;
          window.clearTimeout(scrollSaveTimerRef.current);
          scrollSaveTimerRef.current = window.setTimeout(() => saveScrollTop(y), 150);
        }}
      >
        {hasAnyMenuItems ? (
          <Menu
            mode="inline"
            items={menuItems}
            // `openKeys` is controlled so we can force-open everything
            // during a search. When the query is empty, onOpenChange wires
            // back into userOpenKeys so the user can collapse sections.
            openKeys={effectiveOpenKeys}
            onOpenChange={(keys) => {
              if (query.trim() === '') setUserOpenKeys(keys as string[]);
            }}
            selectable={false}
            className={isMobile ? 'examples-drawer-menu examples-drawer-menu--mobile' : 'examples-drawer-menu'}
            style={{ borderInlineEnd: 'none' }}
            // antd's default inlineIndent of 24px stacks badly under three
            // levels on a narrow screen; trim it on mobile so the deepest
            // labels keep their text room.
            inlineIndent={isMobile ? 16 : 24}
            onClick={({ key }) => {
              const k = key as string;
              if (k.startsWith(RECENT_KEY_PREFIX)) {
                loadProject(k.slice(RECENT_KEY_PREFIX.length));
                return;
              }
              const title = titleByKey.get(k);
              if (title) loadProject(title);
            }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('examplesDrawer.NoMatches', lang)}
            style={{ marginTop: 48 }}
          />
        )}
      </div>
    </Drawer>
  );
});

export default ExamplesDrawer;
